import {loggerFactory} from "@cm/browser-log"
import {
    AnyImageOpHalData,
    CtorArgs as DataCtorArgs,
    ImageOpHalData,
    ImageOpHalDataParameters,
} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal-data"
import {ImageOpHalParameters, UpdateImageOptions} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal-types"
import {RegionLockedHalImageRef} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/region-locked-hal-image-ref"
import {ImageOpDefinition} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/image-op-node"
import {ImageOpContextHal} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-context-hal"
import {Descriptor, MipLevelRegion} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-descriptor"
import {traverse} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/utils/image-op-utils"
import {getMipLevelSize} from "@cm/hal"
import {Size2Like} from "@cm/math"
import {deepEqual, isArrayLike, isClass, isRecord, mapFields, REF_COUNT_DEBUG_ID_INITIAL, TrackedRefCountableBase} from "@cm/utils"

const ENABLE_DEBUG_INFO = true

export type AnyImageOpHal = ImageOpHal<ImageOpDefinition, AnyImageOpHalData>

export const isImageOpHal = (obj: unknown): obj is AnyImageOpHal => {
    return obj instanceof ImageOpHal
}

export type CreateDataArgs<Definition extends ImageOpDefinition, ImageOpHalDataClass extends ImageOpHalData<Definition>> = Omit<
    DataCtorArgs<Definition, ImageOpHalDataClass>,
    "descriptor" | "prevData" | "outputImageReuse" | "updateComplexity" | "invalidateEntireOutputOnParameterChange"
> & {
    prevData: ImageOpHalDataClass | null
}

export abstract class ImageOpHal<Definition extends ImageOpDefinition, ImageOpHalDataClass extends ImageOpHalData<Definition>> extends TrackedRefCountableBase {
    readonly _imageOpKind = "hal" as const

    protected abstract createData(args: CreateDataArgs<Definition, ImageOpHalDataClass>): ImageOpHalDataClass //CreateDataResult<Definition, ImageOpHalDataClass>

    constructor(readonly ctx: ImageOpContextHal) {
        super({
            disposeFn: () => this.dispose(),
        })
        ImageOpHal.addInstance(this)
    }

    protected dispose() {
        this._isDisposed = true
        this.setSourceImages([])
        this.releaseData()
        ImageOpHal.removeInstance(this)
    }

    get data(): ImageOpHalDataClass {
        this.checkNotDisposed()
        if (!this._parameters) {
            throw new Error("Parameters not set yet")
        }
        if (this._data && !this._parametersUpdatedSinceLastDataCreation) {
            return this._data // data is still up to date
        }
        this._parametersUpdatedSinceLastDataCreation = false
        const parametersMappedToCurrentData = this.mapParametersToCurrentData(this._parameters)
        const prevData = this._data
        prevData?.addRef("ImageOpHal.prevData")
        this.releaseData()
        this._data = this.createData({
            ctx: this.ctx,
            parameters: parametersMappedToCurrentData,
            prevData,
        })
        prevData?.release("ImageOpHal.prevData")
        this._data.debugId = this.debugId
        return this._data
    }

    setParameters(parameters: ImageOpHalParameters<Definition>) {
        if (deepEqual(this._parameters, parameters)) {
            return
        }
        this._parameters = parameters
        this.setSourceImages(this.collectSourceImages())
        this.notifyParameterChange()
    }

    get descriptor(): Descriptor {
        this.checkNotDisposed()
        return this.data.descriptor
    }

    get numMipLevels() {
        this.checkNotDisposed()
        return this.data.numMipLevels
    }

    getMipLevelSize(mipLevel: number): Size2Like {
        this.checkNotDisposed()
        if (mipLevel < 0 || mipLevel >= this.numMipLevels) {
            throw new Error(`Mip level ${mipLevel} is out of bounds, must be between 0 and ${this.numMipLevels - 1}`)
        }
        return getMipLevelSize(this.descriptor, mipLevel)
    }

    getUpdatedHalImage(mipLevelRegion: MipLevelRegion, options?: UpdateImageOptions): Promise<RegionLockedHalImageRef> {
        return this.data.getUpdatedHalImage(mipLevelRegion, options)
    }

    private checkNotDisposed() {
        if (this._isDisposed) {
            throw new Error("Attempting to access disposed ImageOpHal")
        }
    }

    private mapParametersToCurrentData(parameters: ImageOpHalParameters<Definition>): ImageOpHalDataParameters<Definition> {
        const traverse = (value: unknown) => {
            if (isImageOpHal(value)) {
                return value.data
            }
            if (value && !isClass(value) && (isRecord(value) || isArrayLike(value))) {
                return mapFields(value, (value: unknown) => traverse(value))
            } else {
                return value
            }
        }
        return traverse(parameters) as ImageOpHalDataParameters<Definition>
    }

    private releaseData() {
        this._data?.release(REF_COUNT_DEBUG_ID_INITIAL)
        this._data = null
    }

    private collectSourceImages() {
        const sourceImages = new Set<AnyImageOpHal>()
        traverse(this._parameters, (value) => {
            if (value instanceof ImageOpHal) {
                sourceImages.add(value)
            }
        })
        return Array.from(sourceImages)
    }

    private setSourceImages(sourceImages: AnyImageOpHal[]) {
        for (const newSourceImage of sourceImages) {
            let info = this._infoBySourceImage.get(newSourceImage)
            if (!info) {
                info = {
                    refCount: 1,
                    lastSeenData: null,
                }
                newSourceImage.addRef("ImageOpFnHal.sourceImage") // we keep a reference to all source images
                newSourceImage.addConsumer(this)
                this._infoBySourceImage.set(newSourceImage, info)
            } else {
                info.refCount++
            }
        }
        for (const oldSourceImage of this._sourceImages) {
            const info = this._infoBySourceImage.get(oldSourceImage)
            if (!info) {
                throw new Error("No info found for source image")
            }
            info.refCount--
            if (info.refCount === 0) {
                this._infoBySourceImage.delete(oldSourceImage)
                oldSourceImage.removeConsumer(this)
                oldSourceImage.release("ImageOpFnHal.sourceImage") // release the kept reference to the source image
            }
        }
        this._sourceImages = sourceImages
    }

    private addConsumer(consumer: AnyImageOpHal) {
        if (this.consumers.has(consumer)) {
            throw new Error("Consumer already added")
        }
        this.consumers.add(consumer)
    }

    private removeConsumer(consumer: AnyImageOpHal) {
        if (!this.consumers.has(consumer)) {
            throw new Error("Consumer not found")
        }
        this.consumers.delete(consumer)
    }

    private notifyParameterChange() {
        if (this._parametersUpdatedSinceLastDataCreation) {
            return
        }
        this._parametersUpdatedSinceLastDataCreation = true
        // notify consumers
        for (const consumer of this.consumers) {
            consumer.notifyParameterChange()
        }
    }

    private static addInstance(instance: AnyImageOpHal) {
        if (ENABLE_DEBUG_INFO) {
            const className = instance.constructor.name
            let debugInfo = ImageOpHal.debugInfoByClassName.get(className)
            if (!debugInfo) {
                debugInfo = {className, instances: new Set()}
                ImageOpHal.debugInfoByClassName.set(className, debugInfo)
            }
            debugInfo.instances.add(instance)
            ImageOpHal.logger.trace(`Creating ImageOpHal ${instance.constructor.name}`, instance)
        }
    }

    private static removeInstance(instance: AnyImageOpHal) {
        if (ENABLE_DEBUG_INFO) {
            const className = instance.constructor.name
            const debugInfo = ImageOpHal.debugInfoByClassName.get(className)
            if (debugInfo) {
                debugInfo.instances.delete(instance)
                if (debugInfo.instances.size === 0) {
                    ImageOpHal.debugInfoByClassName.delete(className)
                }
            }
            ImageOpHal.logger.trace(`Disposing ImageOpHal ${instance.debugId}`, instance)
        }
    }

    static checkNoLockedPages() {
        let lockedPagesFound = false
        ImageOpHal.debugInfoByClassName.forEach((debugInfo) => {
            debugInfo.instances.forEach((instance) => {
                if (instance.data.checkNoLockedPages()) {
                    lockedPagesFound = true
                }
            })
        })
        return lockedPagesFound
    }

    static checkPageIntegrity() {
        let ctx: ImageOpContextHal | null = null
        let _allocatedPages = 0
        for (const [_, debugInfo] of ImageOpHal.debugInfoByClassName) {
            for (const instance of debugInfo.instances) {
                _allocatedPages += instance.data.getNumAllocatedPages()
                ctx ??= instance.ctx
            }
        }
        // if (ctx) {
        //     const usedAtlasPages = ctx.halContext.stats.caches.virtual?.atlas.numUsedPages ?? 0
        //     if (allocatedPages !== usedAtlasPages) {
        //         ImageOpHal.logger.error(`Mismatch between allocated pages (${allocatedPages}) and used atlas pages (${usedAtlasPages}) !`)
        //     }
        // }
    }

    static printDebugInfo() {
        ImageOpHal.debugInfoByClassName.forEach((debugInfo) => {
            ImageOpHal.logger.log(`Class ${debugInfo.className} has ${debugInfo.instances.size} instances:`)
            let index = 0
            for (const instance of debugInfo.instances) {
                const id = instance.debugId ?? `[${index}]`
                ImageOpHal.logger.log(`  *** ${id} (RefCount: ${instance.refCount}) ***`, instance, Array.from(instance.refCountsByDebugId.entries()))
                instance.data.printDebugInfo()
                index++
            }
        })
        ImageOpHalData.printDebugInfo()
    }

    static freeAllPages() {
        ImageOpHal.debugInfoByClassName.forEach((debugInfo) =>
            debugInfo.instances.forEach((instance) => {
                instance.data.freeAllPages()
            }),
        )
    }

    get debugId() {
        return this._debugId
    }

    set debugId(debugId: string | undefined) {
        this._debugId = debugId
        if (this._data) {
            this._data.debugId = debugId
        }
    }

    readonly consumers: Set<AnyImageOpHal> = new Set()

    private _debugId?: string

    private _isDisposed = false // for debugging only
    private _parameters: ImageOpHalParameters<Definition> | null = null
    private _parametersUpdatedSinceLastDataCreation = false
    private _data: ImageOpHalDataClass | null = null

    private _sourceImages: AnyImageOpHal[] = []
    private _infoBySourceImage = new Map<AnyImageOpHal, SourceImageInfo>()

    static debugInfoByClassName = new Map<string, DebugInfo>()
    // TODO: remove static logger, replace with instance logger
    private static readonly logger = loggerFactory.getLogger("image-op-system")
}

type SourceImageInfo = {
    refCount: number
    lastSeenData: AnyImageOpHalData | null
}

type DebugInfo = {
    className: string
    instances: Set<AnyImageOpHal>
}
